import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SentimentVeryDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentVeryDissatisfiedOutlined';
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined';
import SentimentNeutralOutlinedIcon from '@mui/icons-material/SentimentNeutralOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import { getAdaptiveCareLensKey } from './adaptiveQuickLogConfig';

/**
 * HOSPICE LENS VOICE RULES
 * - Comfort and dignity, never progress or achievement
 * - Audience: family + hospice nurse/aide, often across shifts
 * - "Changes worth mentioning to the care team" is the organizing question
 * - Plain words: pain, rest, appetite, alert, peaceful - no clinical jargon,
 *   no developmental language, no "behavior" framing
 * - Small amounts and partial participation are normal, not failures
 */

const DEFAULT_DISTURBANCE_OPTIONS = [
  { label: 'Slept through', value: 'none', icon: CheckRoundedIcon },
  { label: 'Woke once', value: 'once', icon: NightlightRoundIcon },
  { label: 'Woke multiple', value: 'multiple', icon: BoltRoundedIcon },
  { label: 'Up too early', value: 'early', icon: WbSunnyRoundedIcon },
  { label: 'Up too late', value: 'late', icon: ScheduleRoundedIcon },
];

const HOSPICE_REST_OPTIONS = [
  { label: 'Restful', value: 'none', icon: CheckRoundedIcon },
  { label: 'Restless / uncomfortable', value: 'multiple', icon: BoltRoundedIcon },
  { label: 'Dozing on and off', value: 'once', icon: NightlightRoundIcon },
  { label: 'Sleeping most of the day', value: 'late', icon: BedtimeOutlinedIcon },
  { label: 'Awake during the night', value: 'early', icon: WbSunnyRoundedIcon },
];

const DEFAULT_QUALITY_OPTIONS = [
  { value: 'awful', label: 'Awful', iconComponent: SentimentVeryDissatisfiedOutlinedIcon, color: 'sleepQuality.awful' },
  { value: 'poor', label: 'Poor', iconComponent: SentimentDissatisfiedOutlinedIcon, color: 'sleepQuality.poor' },
  { value: 'fair', label: 'Fair', iconComponent: SentimentNeutralOutlinedIcon, color: 'sleepQuality.fair' },
  { value: 'good', label: 'Good', iconComponent: SentimentSatisfiedAltOutlinedIcon, color: 'sleepQuality.good' },
  { value: 'great', label: 'Great', iconComponent: BedtimeOutlinedIcon, color: 'sleepQuality.great' },
];

const HOSPICE_QUALITY_OPTIONS = [
  { value: 'awful', label: 'Very uncomfortable', iconComponent: SentimentVeryDissatisfiedOutlinedIcon, color: 'sleepQuality.awful' },
  { value: 'poor', label: 'Unsettled', iconComponent: SentimentDissatisfiedOutlinedIcon, color: 'sleepQuality.poor' },
  { value: 'fair', label: 'Mixed', iconComponent: SentimentNeutralOutlinedIcon, color: 'sleepQuality.fair' },
  { value: 'good', label: 'Restful', iconComponent: SentimentSatisfiedAltOutlinedIcon, color: 'sleepQuality.good' },
  { value: 'great', label: 'Peaceful', iconComponent: BedtimeOutlinedIcon, color: 'sleepQuality.great' },
];

const DEFAULT_SLEEP_LOG_CONFIG = {
  title: 'Sleep',
  editTitle: 'Edit sleep',
  activeTitle: 'Sleep in progress',
  startTitle: 'Start Sleep',
  editSubtitle: 'Update the recorded sleep details.',
  activeSubtitle: 'Sleep is active right now. End it when done.',
  startSubtitle: 'Tap Start Sleep to begin.',
  activeStatusLabel: 'In progress',
  readyStatusLabel: 'Ready to start',
  startTimeLabel: 'Start time',
  endTimeLabel: 'Wake time',
  addEndTimeText: 'Add wake time when sleep ends.',
  endTimeNotSetText: 'Wake time not set yet',
  eventSectionLabel: 'How was the sleep?',
  qualitySectionLabel: 'Sleep quality',
  notesLabel: 'Notes',
  notesPlaceholder: 'Notes about sleep, wake-ups, routines... (optional)',
  startButtonLabel: 'Start Sleep',
  endButtonLabel: 'End sleep',
  discardButtonLabel: 'Discard session',
  cancelButtonLabel: 'Cancel',
  addEndTimePrompt: 'Add wake time to end sleep.',
  startTimePrompt: 'Set a start time before starting sleep.',
  startedMessage: 'Sleep started',
  endedMessagePrefix: 'Sleep ended for',
  savedTitle: 'Sleep',
  savedTitlePrefix: 'Sleep',
  savedFallbackText: 'Sleep completed',
  eventOptions: DEFAULT_DISTURBANCE_OPTIONS,
  qualityOptions: DEFAULT_QUALITY_OPTIONS,
};

const SLEEP_LOG_CONFIG_BY_LENS = {
  hospice: {
    ...DEFAULT_SLEEP_LOG_CONFIG,
    title: 'Rest & Sleep',
    editTitle: 'Edit rest',
    activeTitle: 'Rest in progress',
    startTitle: 'Start Rest',
    editSubtitle: 'Update the recorded rest details.',
    activeSubtitle: 'Rest is active right now. End it when done.',
    startSubtitle: 'Log rest, naps, and restless periods - day or night.',
    activeStatusLabel: 'Resting',
    readyStatusLabel: 'Ready to start rest',
    startTimeLabel: 'Start time',
    endTimeLabel: 'End time',
    addEndTimeText: 'Add end time when rest ends.',
    endTimeNotSetText: 'End time not set yet',
    eventSectionLabel: 'How was the rest?',
    qualitySectionLabel: 'Rest quality',
    notesLabel: 'Rest notes',
    notesPlaceholder: 'Pain or discomfort, repositioning, breathing changes, what helped...',
    startButtonLabel: 'Start Rest',
    endButtonLabel: 'End rest',
    discardButtonLabel: 'Discard rest',
    addEndTimePrompt: 'Add end time to finish rest.',
    startTimePrompt: 'Set a start time before starting rest.',
    startedMessage: 'Rest started',
    endedMessagePrefix: 'Rest ended for',
    savedTitle: 'Rest & Sleep',
    savedTitlePrefix: 'Rest & Sleep',
    savedFallbackText: 'Rest logged',
    eventOptions: HOSPICE_REST_OPTIONS,
    qualityOptions: HOSPICE_QUALITY_OPTIONS,
  },
};

export const getAdaptiveSleepLogConfig = (child = {}) => (
  SLEEP_LOG_CONFIG_BY_LENS[getAdaptiveCareLensKey(child)] || DEFAULT_SLEEP_LOG_CONFIG
);

